import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from 'components/system/layouts/Layout';
import redirectLinks from 'constants/redirectLinks';
import DeleteButton from 'components/system/atoms/buttons/DeleteButton';
import { Box, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ScreenContext from 'Contexts/Common/ScreenContext';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import COMMON from 'constants/common';
import UrlHelper from 'utils/url.helper';
import PrevNextButton from 'components/system/atoms/buttons/PrevNextButton';
import ReturnButton from 'components/system/atoms/buttons/ReturnButton';
import SavedConfTable from 'components/machineConf/organisms/tables/SavedConfTable';
import { getConfListThunk, setCheckWay, setSaveInfo, setSelectedStationList, setSelectFlg } from 'redux/slices/machineConfSlice';
import { AreaConfGrandType, ConfListType } from 'types/machineConf/machineConfType';
import { animateScroll as scroll } from 'react-scroll';
import ConfirmDialog from 'components/system/organisms/dialogs/ConfirmDialog';
import MsgHelper from 'utils/message.helper';
import MSG from 'constants/messages';
import { REST_API } from 'constants/apiUrls';
import HttpConnection from 'utils/httpConnection';
import InfoDialog from 'components/system/organisms/dialogs/InfoDialog';
import { convertMachineVersionToNumber, renderMachineType } from 'utils/common.helper';
import { handleCallSystemError } from 'redux/slices/commonSlice';
import ERROR_CODE from 'constants/errorCode';
import { StationDetailType } from 'types/machineConf/stationSelectType';
import SelectConfirmDialog from 'components/system/organisms/dialogs/SelectConfirmDialog';
import { CellSizeType } from 'types/machineConf/areaType';
import { themeColor } from 'constants/theme';
import ConfSendDialog from 'components/machineConf/atoms/dialog/ConfSendDialog';

/* ************ Context ************ */
const ScreenContextValue = {
  title: '保存設定一覧',
  screenId: COMMON.SCREEN_ID.SAVED_CONF_LIST,
  currentMenu: COMMON.MENU.MACHINE_CONF,
  initializeState: true,
};
const TABLE_ID = 'dataSavedConfTable';
const MAX_DISPLAY_BAR = 6;

/* ************ Style ************ */
const useStyles = makeStyles()(() => ({
  contents: {
    padding: '16px 32px 28px 27px',
  },
  topArea: {
    display: 'flex',
    justifyContent: 'flex-start',
    height: 98.63,
    '& .returnButton': {
      width: '200px',
      height: '70px',
    },
    '& .description': {
      fontSize: '23px',
      marginLeft: '16px',
    },
    '& .textRed': {
      color: '#F11036',
    },
  },
  previousBtn: {
    margin: '14px 0 12px 0',
    position: 'inherit',
    textAlign: 'center',
    width: '100%',
    height: '24px',
  },
  nextBtn: {
    margin: '12px 0 14px 0',
    textAlign: 'center',
    width: '100%',
    height: '24px',
  },
  deleteBtn: {
    textAlign: 'left',
    width: '144px',
    height: '68px',
    position: 'absolute',
    top: 625,
  },
  contentText: { fontSize: '24px' },
}));

/* ************ Messages ************ */
const confirmDeleteMsg: string = MsgHelper.messageFormat(MSG.INF216, 'チェックした設定');
const successDeleteMsg: string = MsgHelper.messageFormat(MSG.INF304, 'チェックした設定');
const errorDeleteMsg: string = MsgHelper.messageFormat(MSG.INF112, '設定');
const addmsg = '※新サービスモードのプレイ回数が自動計算により筐体側に正しく反映されない可能がございます。正しく反映されていない場合は、筐体側で変更を願います。';

/* ************ Function ************ */
const renderReturnButtonText = (machineType: string | null) => {
  if (machineType === COMMON.MACHINE_TYPE.GRAND || machineType === COMMON.MACHINE_TYPE.MULTI || machineType === COMMON.MACHINE_TYPE.DUO) {
    return (
      <>
        機器設定
        <br />
        詳細
      </>
    );
  }
  return '設定TOP';
};

const renderMessageSendingConf = (machineType: string | null, confReceive: string) => {
  if (
    (machineType === COMMON.MACHINE_TYPE.GRAND ||
      machineType === COMMON.MACHINE_TYPE.MULTI ||
      machineType === COMMON.MACHINE_TYPE.DUO) &&
    confReceive === COMMON.CONF_RECEIVE.WAITING
  ) {
    return <Box className="textRed">選択している機種は受取待機中の為、設定送信できません。</Box>;
  }
  return <></>;
};

const sizeDefault = {
  area0: { width: '0px', height: '0px', border: 'none' },
  area1: { width: '0px', height: '0px', border: 'none' },
  area2: { width: '0px', height: '0px', border: 'none' },
  area3: { width: '0px', height: '0px', border: 'none' },
  area4: { width: '0px', height: '0px', border: 'none' },
  area5: { width: '0px', height: '0px', border: 'none' },
};

const getCellSize = (areaConf: AreaConfGrandType, borderColor?: string): CellSizeType => {
  const isDefault = areaConf && areaConf.areaConf === COMMON.SWITCH.OFF;
  const SIZE_AREA = 210;
  const BORDER_AREA = 1;
  const borderColorStyle = borderColor ?? themeColor.grandDarkColor;

  if (isDefault) {
    return {
      area0: {
        width: '0px',
        height: '0px',
        border: 'none',
      },
      area1: {
        width: '0px',
        height: '0px',
        border: 'none',
      },
      area2: {
        width: '0px',
        height: '0px',
        border: 'none',
      },
      area3: {
        width: `${SIZE_AREA / 3 - BORDER_AREA}px`,
        height: `${SIZE_AREA / 2 - BORDER_AREA}px`,
        border: 'none',
      },
      area4: {
        width: '0px',
        height: '0px',
        border: 'none',
      },
      area5: {
        width: '0px',
        height: '0px',
        border: 'none',
      },
    };
  }
  const line1 = (areaConf.line1 * SIZE_AREA) / 100;
  const line2 = (areaConf.line2 * SIZE_AREA) / 100;
  const line3 = (areaConf.line3 * SIZE_AREA) / 100;
  let areaWidth0 = line1;
  areaWidth0 = areaWidth0 ? areaWidth0 - BORDER_AREA * 2 : 0;
  let areaHeight0 = SIZE_AREA - line3;
  areaHeight0 = areaHeight0 ? areaHeight0 - BORDER_AREA * 2 : 0;
  const isArea0NoneBorder = [areaWidth0, areaHeight0].includes(0);
  let areaWidth1 = SIZE_AREA - (line1 + (SIZE_AREA - line2));
  areaWidth1 = areaWidth1 ? areaWidth1 - BORDER_AREA * 2 : 0;
  let areaHeight1 = SIZE_AREA - line3;
  areaHeight1 = areaHeight1 ? areaHeight1 - BORDER_AREA * 2 : 0;
  const isArea1NoneBorder = [areaWidth1, areaHeight1].includes(0);
  let areaWidth2 = SIZE_AREA - line2;
  areaWidth2 = areaWidth2 ? areaWidth2 - BORDER_AREA * 2 : 0;
  let areaHeight2 = SIZE_AREA - line3;
  areaHeight2 = areaHeight2 ? areaHeight2 - BORDER_AREA * 2 : 0;
  const isArea2NoneBorder = [areaWidth2, areaHeight2].includes(0);
  let areaWidth3 = line1;
  areaWidth3 = areaWidth3 ? areaWidth3 - BORDER_AREA * 2 : 0;
  let areaHeight3 = line3;
  areaHeight3 = areaHeight3 ? areaHeight3 - BORDER_AREA * 2 : 0;
  const isArea3NoneBorder = [areaWidth3, areaHeight3].includes(0);
  let areaWidth4 = SIZE_AREA - (line1 + (SIZE_AREA - line2));
  areaWidth4 = areaWidth4 ? areaWidth4 - BORDER_AREA * 2 : 0;
  let areaHeight4 = line3;
  areaHeight4 = areaHeight4 ? areaHeight4 - BORDER_AREA * 2 : 0;
  const isArea4NoneBorder = [areaWidth4, areaHeight4].includes(0);
  let areaWidth5 = SIZE_AREA - line2;
  areaWidth5 = areaWidth5 ? areaWidth5 - BORDER_AREA * 2 : 0;
  let areaHeight5 = line3;
  areaHeight5 = areaHeight5 ? areaHeight5 - BORDER_AREA * 2 : 0;
  const isArea5NoneBorder = [areaWidth5, areaHeight5].includes(0);

  return {
    area0: {
      width: `${areaWidth0}px`,
      height: `${areaHeight0}px`,
      border: isArea0NoneBorder ? 'none' : `1px solid ${borderColorStyle}`,
    },
    area1: {
      width: `${areaWidth1}px`,
      height: `${areaHeight1}px`,
      border: isArea1NoneBorder ? 'none' : `1px solid ${borderColorStyle}`,
    },
    area2: {
      width: `${areaWidth2}px`,
      height: `${areaHeight2}px`,
      border: isArea2NoneBorder ? 'none' : `1px solid ${borderColorStyle}`,
    },
    area3: {
      width: `${areaWidth3}px`,
      height: `${areaHeight3}px`,
      border: isArea3NoneBorder ? 'none' : `1px solid ${borderColorStyle}`,
    },
    area4: {
      width: `${areaWidth4}px`,
      height: `${areaHeight4}px`,
      border: isArea4NoneBorder ? 'none' : `1px solid ${borderColorStyle}`,
    },
    area5: {
      width: `${areaWidth5}px`,
      height: `${areaHeight5}px`,
      border: isArea5NoneBorder ? 'none' : `1px solid ${borderColorStyle}`,
    },
  };
};

const SavedConf: React.FC<React.PropsWithChildren<unknown>> = () => {
  /* ************ hooks ************ */
  const { classes } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [openConfirmMsg, setOpenConfirmMsg] = useState<boolean>(false);
  const [openSuccessMsg, setOpenSuccessMsg] = useState<boolean>(false);
  const [openErrorMsg, setOpenErrorMsg] = useState<boolean>(false);
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});
  // BNAL-883対応 コンポーネントを ConfirmDialogから ConfSendDialogへ変更
  const [openConfSendDialog, setOpenConfSendDialog] = useState(false);
  const [openDialogINF203, setOpenDialogINF203] = useState<boolean>(false);
  const [openDialogINF243, setOpenDialogINF243] = useState<boolean>(false);
  const [openDialogINF244, setOpenDialogINF244] = useState<boolean>(false);
  const [openDialogINF246, setOpenDialogINF246] = useState<boolean>(false);
  const [openDialogINF249, setOpenDialogINF249] = useState<boolean>(false);
  const [openDialogINF250, setOpenDialogINF250] = useState<boolean>(false);
  const [openDialogINF256, setOpenDialogINF256] = useState<boolean>(false);
  const [openDialogINF263, setOpenDialogINF263] = useState<boolean>(false);
  const [openDialogINF264, setOpenDialogINF264] = useState<boolean>(false);
  const [openDialogINF265, setOpenDialogINF265] = useState<boolean>(false);
  const [openDialogINF269, setOpenDialogINF269] = useState<boolean>(false);
  const [openDialogINF270, setOpenDialogINF270] = useState<boolean>(false);
  const [openDialogINF271, setOpenDialogINF271] = useState<boolean>(false);
  const [selectedItemValue, setSelectedItemValue] = useState<string>('');
  const [isSelectAllSetting, setIsSelectAllSetting] = useState<string>('1');
  const [operationFlg, setOperationFlg] = useState<string>('');
  const [machineVersion, setMachineVersion] = useState<string>('');
  const [selectMachineItem, setSelectMachineItem] = useState<ConfListType>({
    value: '',
    name: '',
  leftRight: '',
  machineType: '',
  machineVersion: '',
  operationExamplesFlg: ''})

  /* ************ state/redux ************ */
  const savedConfList: Array<ConfListType> = useAppSelector((state) => state.machineConf.savedConfList);
  const { selectedStation, selectedStationList } = useAppSelector((state) => state.machineConf);
  const { versionNo, confReceive, boardSerial, confId: selfConfId, boardStation, leftRight } = selectedStationList[0];
  const isClena3 = selectedStation.machineType === COMMON.MACHINE_TYPE.CLENA3;
  const isJack = selectedStation.machineType === COMMON.MACHINE_TYPE.JACK;
  // BNAL881の一時的な対応 仕様を戻すときはこのコメントで囲われた行を削除してください start
  const isMulti = selectedStation.machineType === COMMON.MACHINE_TYPE.MULTI;
  // BNAL881の一時的な対応 仕様を戻すときはこのコメントで囲われた行を削除してください end
  const isDuo = selectedStation.machineType === COMMON.MACHINE_TYPE.DUO;

  useEffect(() => {
    handleInitialDisplay();
  }, []);

  /* ************ Function ************ */
  const redirectByReflection = (confId: string) => {
    if (selectedStation.machineType === COMMON.MACHINE_TYPE.JACK) {
      navigate(UrlHelper.convertQueryUrlFrontEnd(redirectLinks.MCONF_JACK_CONF_TOP, {
        selectConfId: confId,
      }), {
        state: { execDiv: COMMON.EXEC_DIV.SAVED_CONF },
      });
    } else {
      navigate(UrlHelper.convertQueryUrlFrontEnd(redirectLinks.MCONF_STATION_CONF_TOP, {
        selectConfId: confId,
      }), {
        state: { execDiv: COMMON.EXEC_DIV.SAVED_CONF },
      });
    }
  };

  const redirectBySendingConf = () => {
    setOpenConfSendDialog(true);
  };

  // BNAL881 クレナマルチの保存設定一覧_モーダル変更対応
  // BNAL881 元のモーダルをを表示する仕様に戻すときはこのコメントで囲われた行のコメントアウトを解除してください start
  //const handleMachineVersionComparison = (isSelectAll: string) => 
  // BNAL881 元のモーダルをを表示する仕様に戻すときはこのコメントで囲われた行のコメントアウトを解除してください end
  // BNAL881の一時的な対応 仕様を戻すときはこのコメントで囲われた行を削除してください start
  const handleMachineVersionComparison = (isSelectAll: string, multiMachineVersion: string = '') => 
  // BNAL881の一時的な対応 仕様を戻すときはこのコメントで囲われた行を削除してください end
  {
    const machineVersionSet = new Set();
    selectedStationList.forEach((station) => machineVersionSet.add(station.machineVersion));
    const stationMachineVersion = convertMachineVersionToNumber(selectedStation.machineVersion);
    const selectedConfMachineVersion = convertMachineVersionToNumber(machineVersion === '' ? null : machineVersion);
    // BNAL881の一時的な対応 仕様を戻すときはこのコメントで囲われた行を削除してください start
    const multiSelectedConfMachineVersion = convertMachineVersionToNumber(multiMachineVersion === '' ? null : multiMachineVersion);
    // BNAL881の一時的な対応 仕様を戻すときはこのコメントで囲われた行を削除してください end
    setIsSelectAllSetting(isSelectAll); // 全設定送信か一部設定送信かのフラグをセット
    // 状態の変更を監視してログを出力
    // 各種ダイアログを閉じる
    setOpenDialogINF256(false);
    setOpenDialogINF263(false);
    setOpenDialogINF264(false);
    setOpenDialogINF269(false);
  
    if (isJack) {
      dispatch(setSaveInfo({ isSelectAllSetting: isSelectAll, operationFlg }));
      redirectByReflection(selectedItemValue);
    // BNAL881 元のモーダルをを表示する仕様に戻すときはこのコメントで囲われた行のコメントアウトを解除してください start
    //} else if (stationMachineVersion === null || selectedConfMachineVersion === null) {
    // BNAL881 元のモーダルをを表示する仕様に戻すときはこのコメントで囲われた行のコメントアウトを解除してください end
    // BNAL881の一時的な対応 仕様を戻すときはこのコメントで囲われた行を削除してください start
    } else if (stationMachineVersion === null || selectedConfMachineVersion === null || multiSelectedConfMachineVersion === null) {
    // BNAL881の一時的な対応 仕様を戻すときはこのコメントで囲われた行を削除してください end
      dispatch(handleCallSystemError({ errorNo: ERROR_CODE.NOT_FOUND }));
    } else if (isClena3 && machineVersionSet.size > 1) {
      setOpenDialogINF244(true);
    // BNAL881 元のモーダルをを表示する仕様に戻すときはこのコメントで囲われた行のコメントアウトを解除してください start
    //} else if (stationMachineVersion > selectedConfMachineVersion) {
    // BNAL881 元のモーダルをを表示する仕様に戻すときはこのコメントで囲われた行のコメントアウトを解除してください end
    // BNAL881の一時的な対応 仕様を戻すときはこのコメントで囲われた行を削除してください start
    } else if (stationMachineVersion > (isMulti && operationFlg !== '1' ? multiSelectedConfMachineVersion : selectedConfMachineVersion)) {
    // BNAL881の一時的な対応 仕様を戻すときはこのコメントで囲われた行を削除してください end
      if (isClena3) {
        setOpenDialogINF243(true);
      } else if (isDuo) {
        setOpenDialogINF270(true);
      } else {
        setOpenDialogINF249(true);
      }
    // BNAL881 元のモーダルをを表示する仕様に戻すときはこのコメントで囲われた行のコメントアウトを解除してください start
    //} else if (stationMachineVersion < selectedConfMachineVersion) {
    // BNAL881 元のモーダルをを表示する仕様に戻すときはこのコメントで囲われた行のコメントアウトを解除してください end
    // BNAL881の一時的な対応 仕様を戻すときはこのコメントで囲われた行を削除してください start
    } else if (stationMachineVersion < (isMulti && operationFlg !== '1' ? multiSelectedConfMachineVersion : selectedConfMachineVersion)) {
    // BNAL881の一時的な対応 仕様を戻すときはこのコメントで囲われた行を削除してください end
      if (isClena3) {
        setOpenDialogINF246(true);
      } else if (isDuo) {
        setOpenDialogINF271(true);
      } else {
        setOpenDialogINF250(true);
      }
    } else if (isClena3) {
      dispatch(setSaveInfo({ isSelectAllSetting: isSelectAll, operationFlg }));
      redirectByReflection(selectedItemValue);
    } else {
      // Version不一致が起きていないグラン・マルチ・グランデュオの場合に以下処理が走る
      redirectBySendingConf();
    }
  };

  const handleClose = () => {
    setOpenDialogINF256(false);
    setOpenDialogINF263(false);
    setOpenDialogINF264(false);
    setOpenDialogINF265(false);
    setOpenDialogINF269(false);
  };

  const handleCloseInfoDialog = () => {
    setOpenDialogINF203(false);
    // state(operationFlg,checkWay)の状況を初期化する
    dispatch(setSelectFlg({ operationFlg: '0', checkWay: '1', isSelectAllSetting: '1' }));
    let url = redirectLinks.MCONF_GRAND_HOME_DETAIL;
    if (selectedStation.machineType === COMMON.MACHINE_TYPE.MULTI) {
      url = redirectLinks.MCONF_MULTI_HOME_DETAIL;
    } else if (selectedStation.machineType === COMMON.MACHINE_TYPE.DUO) {
      url = redirectLinks.MCONF_DUO_HOME_DETAIL;
    }
    navigate(UrlHelper.convertQueryUrlFrontEnd(url, {
      selectConfId: selfConfId.toString(),
    }), {
      state: {
        from: location.pathname,
      },
    });
  };

  // BNAL-883対応 コンポーネントを ConfirmDialogから ConfSendDialogへ変更
  const handleOkConfSendDialog = async (confId: string) => {
    dispatch(setSaveInfo({ isSelectAllSetting, operationFlg }));
    const http = new HttpConnection({ dispatch });
    let url = REST_API.MACHINE_CONF.SEND_CONF_TO_GRAND;
    if (selectedStation.machineType === COMMON.MACHINE_TYPE.GRAND) {
      if (isSelectAllSetting === '0') {
        // confIdを用いてareaConfの内容を取得。取得情報を用いて再度APIを送信
        const areaConf = await http.get<AreaConfGrandType>(REST_API.MACHINE_CONF.GET_AREA_CONF, { confId });
        const sizeAreaFromAreaConf: CellSizeType = areaConf ? getCellSize(areaConf) : sizeDefault;
        const sizeAreaObj = {
          area0: sizeAreaFromAreaConf.area3.border !== 'none',
          area1: sizeAreaFromAreaConf.area4.border !== 'none',
          area2: sizeAreaFromAreaConf.area5.border !== 'none',
          area3: sizeAreaFromAreaConf.area0.border !== 'none',
          area4: sizeAreaFromAreaConf.area1.border !== 'none',
          area5: sizeAreaFromAreaConf.area2.border !== 'none',
        }
        await http.post(url, {
          confId,
          boardSerial,
          selfConfId,
          versionNo,
          isSelectAllSetting,
          operationFlg,
          sizeAreaObj,
        });
      } else {
        await http.post(url, {
          confId,
          boardSerial,
          selfConfId,
          versionNo,
          isSelectAllSetting,
          operationFlg,
        });
      }
    }
    if (selectedStation.machineType === COMMON.MACHINE_TYPE.MULTI) {
      url = REST_API.MACHINE_CONF.SEND_CONF_TO_MULTI;
      await http.post(url, {
        confId,
        boardStation,
        selfConfId,
        versionNo,
        isSelectAllSetting,
        operationFlg,
      });
    }
    if (selectedStation.machineType === COMMON.MACHINE_TYPE.DUO) {
      url = REST_API.MACHINE_CONF.SEND_CONF_TO_DUO;
      await http.post(url, {
        confId,
        boardSerial,
        selfConfId,
        versionNo,
        isSelectAllSetting,
        operationFlg,
        leftRight,
      });
    }
    const newSelectedStationItem: StationDetailType = {
      ...selectedStationList[0],
      confReceive: COMMON.CONF_RECEIVE.WAITING,
    };
    dispatch(setSelectedStationList([newSelectedStationItem]));
    setOpenDialogINF203(true);
  };

  /* ************ Event ************ */
  // No1. Initial display
  const handleInitialDisplay = async () => {
    setCheckedItems({});
    dispatch(getConfListThunk({ machineType: selectedStation.machineType }));
  };

  // No2. Back to the station setting TOP screen
  const handleClickReturn = useCallback(() => {
    if (selectedStation.machineType === COMMON.MACHINE_TYPE.JACK) {
      navigate(UrlHelper.convertQueryUrlFrontEnd(redirectLinks.MCONF_JACK_CONF_TOP, {
        selectConfId: 'none',
      }));
    } else if (selectedStation.machineType === COMMON.MACHINE_TYPE.GRAND) {
      navigate(UrlHelper.convertQueryUrlFrontEnd(redirectLinks.MCONF_GRAND_HOME_DETAIL, {
        selectConfId: selfConfId.toString(),
      }));
    } else if (selectedStation.machineType === COMMON.MACHINE_TYPE.MULTI) {
      navigate(UrlHelper.convertQueryUrlFrontEnd(redirectLinks.MCONF_MULTI_HOME_DETAIL, {
        selectConfId: selfConfId.toString(),
      }));
    } else if (selectedStation.machineType === COMMON.MACHINE_TYPE.DUO) {
      navigate(UrlHelper.convertQueryUrlFrontEnd(redirectLinks.MCONF_DUO_HOME_DETAIL, {
        selectConfId: selfConfId.toString(),
      }));
    } else {
      navigate(UrlHelper.convertQueryUrlFrontEnd(redirectLinks.MCONF_STATION_CONF_TOP, {
        selectConfId: 'none',
      }));
    }
  }, []);

  // No3. Scroll up
  const handleClickPrev = useCallback(() => {
    scroll.scrollMore(-58, {
      duration: 50,
      delay: 0,
      containerId: TABLE_ID,
    });
  }, []);

  // No4. Handle Reflect button
  const handleClickReflect = useCallback(async (selectedItem: ConfListType) => {
    setSelectedItemValue(selectedItem.value);
    setMachineVersion(selectedItem.machineVersion ?? '');
    setOperationFlg(selectedItem.operationExamplesFlg ?? '0');
    // 運営事例からの保存設定の場合
    if (selectedItem.operationExamplesFlg === '1'){
      setOpenDialogINF256(true);
    }
    // 運営事例からの保存設定ではないかつ、機種がクレナグラン・クレナマルチの場合
    if (
      // BNAL881 元のモーダルをを表示する仕様に戻すときはこのコメントで囲われた行のコメントアウトを解除してください start
      //(selectedStation.machineType === COMMON.MACHINE_TYPE.GRAND || selectedStation.machineType === COMMON.MACHINE_TYPE.MULTI) &&
      // BNAL881 元のモーダルをを表示する仕様に戻すときはこのコメントで囲われた行のコメントアウトを解除してください end
      // BNAL881の一時的な対応 仕様を戻すときはこのコメントで囲われた行を削除してください start
      selectedStation.machineType === COMMON.MACHINE_TYPE.GRAND &&
      // BNAL881の一時的な対応 仕様を戻すときはこのコメントで囲われた行を削除してください end
      selectedItem.operationExamplesFlg !== '1'
    ) {
      setOpenDialogINF264(true);
    // BNAL881の一時的な対応 仕様を戻すときはこのコメントで囲われた行を削除してください start
    } else if (
      selectedStation.machineType === COMMON.MACHINE_TYPE.MULTI &&
      selectedItem.operationExamplesFlg !== '1'
    ) {
      handleMachineVersionComparison('1', selectedItem.machineVersion ?? '');
    // BNAL881の一時的な対応 仕様を戻すときはこのコメントで囲われた行を削除してください end
    } else if (
      selectedStation.machineType === COMMON.MACHINE_TYPE.DUO &&
      selectedItem.operationExamplesFlg !== '1'
    ) {
      setOpenDialogINF269(true);
    }
    // 運営事例からの保存設定ではないかつ、機種がクレナ3・ジャックの場合
    if (
      (selectedStation.machineType === COMMON.MACHINE_TYPE.CLENA3 || selectedStation.machineType === COMMON.MACHINE_TYPE.JACK) &&
      selectedItem.operationExamplesFlg !== '1'
    ) {
      setOpenDialogINF263(true);
    }
  }, []);

  // No5. Handle Setting Detail button
  const handleClickSettingDetail = useCallback((selectedItem: ConfListType) => {
    if (selectedItem.machineType === COMMON.MACHINE_TYPE.JACK) {
      navigate(
        UrlHelper.convertQueryUrlFrontEnd(redirectLinks.MCONF_JACK_HOME_DETAIL, {
          selectConfId: selectedItem.value,
        }),
        {
          state: {
            from: location.pathname,
          },
        },
      );
    } else if (selectedItem.machineType === COMMON.MACHINE_TYPE.GRAND) {
      if (selectedItem.operationExamplesFlg === '1') {
        // 運営事例からの保存設定の場合は、一部設定確認を選択させる
        dispatch(setCheckWay('0'));
        navigate(
          UrlHelper.convertQueryUrlFrontEnd(redirectLinks.MCONF_GRAND_HOME_DETAIL, {
            selectConfId: selectedItem.value,
          }),
          {
            state: {
              from: location.pathname,
            },
          },
        );
      } else {
        // 一部設定確認か全設定確認かを選択させるモーダル表示
        setOpenDialogINF265(true);
        setSelectMachineItem(selectedItem);
      }
    } else if (selectedItem.machineType === COMMON.MACHINE_TYPE.MULTI) {
      if (selectedItem.operationExamplesFlg === '1') {
        dispatch(setCheckWay('0'));
        navigate(
          UrlHelper.convertQueryUrlFrontEnd(redirectLinks.MCONF_MULTI_HOME_DETAIL, {
            selectConfId: selectedItem.value,
          }),
          {
            state: {
              from: location.pathname,
            },
          },
        );
      } else {
        // BNAL881 クレナマルチの保存設定一覧_モーダル変更対応
        // 元のモーダルをを表示する仕様に戻すときはこのコメントで囲われた行のコメントアウトを解除してください start
        // 一部設定確認か全設定確認かを選択させるモーダル表示
        //setOpenDialogINF265(true);
        //setSelectMachineItem(selectedItem);
        // 元のモーダルをを表示する仕様に戻すときはこのコメントで囲われた行のコメントアウトを解除してください end

        // BNAL881の一時的な対応 仕様を戻すときはこのコメントで囲われた行を削除してください start
        // 固定で全部設定確認を表示する
        dispatch(setCheckWay('1'));
        navigate(
          UrlHelper.convertQueryUrlFrontEnd(redirectLinks.MCONF_MULTI_HOME_DETAIL, {
            selectConfId: selectedItem.value,
          }),
          {
            state: {
              from: location.pathname,
            },
          },
        );
        // BNAL881の一時的な対応 仕様を戻すときはこのコメントで囲われた行を削除してください end
      }
    } else if (selectedItem.machineType === COMMON.MACHINE_TYPE.DUO) {
      if (selectedItem.operationExamplesFlg === '1') {
        // 運営事例からの保存設定の場合は、一部設定確認を選択させる
        dispatch(setCheckWay('0'));
        navigate(
          UrlHelper.convertQueryUrlFrontEnd(redirectLinks.MCONF_DUO_HOME_DETAIL, {
            selectConfId: selectedItem.value,
          }),
          {
            state: {
              from: location.pathname,
            },
          },
        );
      } else {
        // 一部設定確認か全設定確認かを選択させるモーダル表示
        setOpenDialogINF265(true);
        setSelectMachineItem(selectedItem);
      }
    } else {
      navigate(
        UrlHelper.convertQueryUrlFrontEnd(redirectLinks.MCONF_STATION_HOME_DETAIL, {
          selectConfId: selectedItem.value,
        }),
        {
          state: {
            from: location.pathname,
          },
        },
      );
    }
  }, []);

  // No6. Scroll down
  const handleClickNext = useCallback(() => {
    scroll.scrollMore(58, {
      duration: 50,
      delay: 0,
      containerId: TABLE_ID,
    });
  }, []);

  // No7. Delete
  const handleSelectedItemChange = useCallback((checkedItem: ConfListType) => {
    setCheckedItems((prevState: Record<string, boolean>) => ({
      ...prevState,
      [checkedItem.value]: !prevState[checkedItem.value],
    }));
  }, []);

  const handleOkDeleteConfirmDlg = async () => {
    const confIdList: Array<string> = [];
    Object.entries(checkedItems).forEach(([confId, isChecked]) => {
      if (isChecked) {
        confIdList.push(confId);
      }
    });

    setOpenConfirmMsg(false);
    await deleteSavedConf(confIdList);
    handleInitialDisplay();
    setOpenSuccessMsg(true);
  };

  const deleteSavedConf = async (confIdList: Array<string>) => {
    const http = new HttpConnection({ dispatch });
    await http.put(REST_API.MACHINE_CONF.DELETE_SAVED_CONF, { confIdList });
  };

  const handleClickDelete = () => {
    const confIdList: Array<string> = [];
    Object.entries(checkedItems).forEach(([confId, isChecked]) => {
      if (isChecked) {
        confIdList.push(confId);
      }
    });
    if (confIdList.length < 1) {
      setOpenErrorMsg(true);
    } else {
      setOpenConfirmMsg(true);
    }
  };

  const handleOkReflectConfirmDlg = async () => {
    setOpenDialogINF243(false);
    setOpenDialogINF244(false);
    setOpenDialogINF246(false);
    setOpenDialogINF249(false);
    setOpenDialogINF250(false);
    setOpenDialogINF270(false);
    setOpenDialogINF271(false);
    dispatch(setSaveInfo({ isSelectAllSetting, operationFlg }));
    redirectByReflection(selectedItemValue);
  };

  const handleConfirmDetail = (inputValue: string) => {
    dispatch(setCheckWay(inputValue));
    if (selectMachineItem.machineType === COMMON.MACHINE_TYPE.GRAND) {
      navigate(
        UrlHelper.convertQueryUrlFrontEnd(redirectLinks.MCONF_GRAND_HOME_DETAIL, {
          selectConfId: selectMachineItem.value,
        }),
        {
          state: {
            from: location.pathname,
          },
        },
      );
    } else if (selectMachineItem.machineType === COMMON.MACHINE_TYPE.MULTI) {
      navigate(
        UrlHelper.convertQueryUrlFrontEnd(redirectLinks.MCONF_MULTI_HOME_DETAIL, {
          selectConfId: selectMachineItem.value,
        }),
        {
          state: {
            from: location.pathname,
          },
        },
      );
    } else if (selectMachineItem.machineType === COMMON.MACHINE_TYPE.DUO) {
      navigate(
        UrlHelper.convertQueryUrlFrontEnd(redirectLinks.MCONF_DUO_HOME_DETAIL, {
          selectConfId: selectMachineItem.value,
        }),
        {
          state: {
            from: location.pathname,
          },
        },
      );
    }
  };

  return (
    <ScreenContext.Provider value={ScreenContextValue}>
      <Layout>
        <Box className={classes.contents}>
          <Box className={classes.topArea}>
            <Box className="returnButton">
              <ReturnButton onClick={handleClickReturn}>
                {renderReturnButtonText(selectedStation.machineType)}
              </ReturnButton>
            </Box>
            <Box className="description">
              選択している機種の保存設定が一覧に表示されています。
              <br />
              選択中の機種：{renderMachineType(selectedStation.machineType)}
              {renderMessageSendingConf(selectedStation.machineType, confReceive)}
            </Box>
          </Box>
          {savedConfList.length > 0 && (
            <>
              <Grid className={classes.previousBtn}>
                <PrevNextButton isUp className="prevScroll" onClick={handleClickPrev} />
              </Grid>
              <SavedConfTable
                savedConfList={savedConfList}
                tableId={TABLE_ID}
                confReceive={confReceive}
                machineType={selectedStation.machineType}
                handleSelectedItemChange={handleSelectedItemChange}
                handleClickReflect={handleClickReflect}
                handleClickSettingDetail={handleClickSettingDetail}
              />
              <Grid className={classes.nextBtn}>
                {MAX_DISPLAY_BAR < savedConfList.length && (
                  <PrevNextButton className="nextScroll" onClick={handleClickNext} />
                )}
              </Grid>
              <Grid className={classes.deleteBtn}>
                <DeleteButton onClick={handleClickDelete}>
                  チェック
                  <br />
                  して削除
                </DeleteButton>
              </Grid>
            </>
          )}
        </Box>

        <ConfirmDialog
          open={openConfirmMsg}
          msg={confirmDeleteMsg}
          onOk={handleOkDeleteConfirmDlg}
          onCancel={() => setOpenConfirmMsg(false)}
          addClass={{
            contentText: classes.contentText,
          }}
        />
        {/* BNAL-883対応 コンポーネントを ConfirmDialogから ConfSendDialogへ変更 */}
        {/* onForceはBNAL-287対応で無効化しているがpropsで渡す必要があるため空のファンクションを設定 */}
        {/* BNAL-884対応 メッセージの追加 addmsg */}
        <ConfSendDialog
          open={openConfSendDialog}
          onForce={() => {}}
          onOk={() => handleOkConfSendDialog(selectedItemValue)}
          onCancel={() => setOpenConfSendDialog(false)}
          addmsg={selectedStation.machineType === COMMON.MACHINE_TYPE.GRAND ? addmsg : ''}
        />
        <InfoDialog open={openDialogINF203} msg={MSG.INF203} closeFunc={handleCloseInfoDialog} closeBtnLabel="閉じる" />
        <ConfirmDialog
          open={openDialogINF243}
          msg={MsgHelper.messageFormat(MSG.INF243)}
          onOk={handleOkReflectConfirmDlg}
          onCancel={() => setOpenDialogINF243(false)}
        />
        <ConfirmDialog
          open={openDialogINF244}
          msg={MsgHelper.messageFormat(MSG.INF244)}
          onOk={handleOkReflectConfirmDlg}
          onCancel={() => setOpenDialogINF244(false)}
        />
        <ConfirmDialog
          open={openDialogINF246}
          msg={MsgHelper.messageFormat(MSG.INF246)}
          onOk={handleOkReflectConfirmDlg}
          onCancel={() => setOpenDialogINF246(false)}
        />
        <ConfirmDialog
          open={openDialogINF249}
          msg={MsgHelper.messageFormat(MSG.INF249)}
          onOk={redirectBySendingConf}
          onCancel={() => setOpenDialogINF249(false)}
        />
        <ConfirmDialog
          open={openDialogINF250}
          msg={MsgHelper.messageFormat(MSG.INF250)}
          onOk={redirectBySendingConf}
          onCancel={() => setOpenDialogINF250(false)}
        />
        <ConfirmDialog
          open={openDialogINF256}
          msg={MsgHelper.messageFormat(MSG.INF256)}
          onOk={() => handleMachineVersionComparison('0')}
          onCancel={handleClose}
        />
        <ConfirmDialog
          open={openDialogINF270}
          msg={MsgHelper.messageFormat(MSG.INF270)}
          onOk={redirectBySendingConf}
          onCancel={() => setOpenDialogINF270(false)}
        />
        <ConfirmDialog
          open={openDialogINF271}
          msg={MsgHelper.messageFormat(MSG.INF271)}
          onOk={redirectBySendingConf}
          onCancel={() => setOpenDialogINF271(false)}
        />
        <SelectConfirmDialog
          open={openDialogINF263}
          title="設定反映の方法を選択してください。"
          msg={MsgHelper.messageFormat(MSG.INF263)}
          leftSelect={() => handleMachineVersionComparison('1')}
          rightSelect={() => handleMachineVersionComparison('0')}
          onClose={handleClose}
          allSettingSendLabel="全設定反映"
          partSettingSendLabel="一部設定反映"
        />
        <SelectConfirmDialog
          open={openDialogINF264}
          title="設定送信の方法を選択してください。"
          msg={MsgHelper.messageFormat(MSG.INF264)}
          leftSelect={() => handleMachineVersionComparison('1')}
          rightSelect={() => handleMachineVersionComparison('0')}
          onClose={handleClose}
          allSettingSendLabel="全設定送信"
          partSettingSendLabel="一部設定送信"
        />
        <SelectConfirmDialog
          open={openDialogINF265}
          title="設定詳細確認方法を選択してください。"
          msg={MsgHelper.messageFormat(MSG.INF265)}
          leftSelect={() => handleConfirmDetail('1')}
          rightSelect={() => handleConfirmDetail('0')}
          onClose={handleClose}
          allSettingSendLabel="全設定確認"
          partSettingSendLabel="一部設定確認"
        />
        <SelectConfirmDialog
          open={openDialogINF269}
          title="設定送信の方法を選択してください。"
          msg={MsgHelper.messageFormat(MSG.INF269)}
          leftSelect={() => handleMachineVersionComparison('1')}
          rightSelect={() => handleMachineVersionComparison('0')}
          onClose={handleClose}
          allSettingSendLabel="全設定送信"
          partSettingSendLabel="一部設定送信"
        />
        <InfoDialog
          open={openSuccessMsg}
          msg={successDeleteMsg}
          closeFunc={() => setOpenSuccessMsg(false)}
          closeBtnLabel="OK"
        />
        <InfoDialog open={openErrorMsg} msg={errorDeleteMsg} closeFunc={() => setOpenErrorMsg(false)} />
      </Layout>
    </ScreenContext.Provider>
  );
};

export default SavedConf;
